<!-- Conversations toggle -->
<button mat-icon-button (click)="openPanel()" #conversationsOrigin>
    @if (unreadCount > 0) {
        <span
            class="absolute left-0 right-0 top-0 flex h-3 items-center justify-center"
        >
            <span
                class="ml-4 mt-2.5 flex h-4 min-w-4 shrink-0 items-center justify-center rounded-full bg-sky-600 px-1 text-xs font-medium text-indigo-50"
            >
                {{ unreadCount }}
            </span>
        </span>
    }
    <mat-icon [svgIcon]="'heroicons_outline:cloud'"></mat-icon>
</button>

<!-- Conversations panel -->
<ng-template #conversationsPanel>
    <div
        class="fixed inset-0 flex flex-col overflow-hidden shadow-sm sm:static sm:inset-auto sm:w-90 sm:min-w-90 sm:rounded-2xl"
    >
        <!-- Header -->
        <div
            class="flex shrink-0 items-center bg-primary py-4 pl-6 pr-4 text-on-primary"
        >
            <div class="-ml-1 mr-3 sm:hidden">
                <button mat-icon-button (click)="closePanel()">
                    <mat-icon
                        class="text-current icon-size-5"
                        [svgIcon]="'heroicons_solid:x-mark'"
                    ></mat-icon>
                </button>
            </div>
            <div class="text-lg font-medium leading-10">Conversaciones</div>
            <div class="ml-auto">
                <button
                    class="dark:text-white"
                    mat-icon-button
                    [disabled]="unreadCount === 0"
                    [matTooltip]="'Marcar todas como leídas'"
                    (click)="markAllAsRead()"
                >
                    <mat-icon
                        class="text-current icon-size-5"
                        [svgIcon]="'heroicons_solid:envelope-open'"
                    ></mat-icon>
                </button>
            </div>
        </div>

        <!-- Content -->
        <div
            class="bg-card relative flex flex-auto flex-col divide-y overflow-y-auto sm:max-h-120"
        >
            <!-- Conversations -->
            @for (
                conversation of conversations;
                track trackByFn($index, conversation)
            ) {
                <div
                    class="group flex hover:bg-gray-50 dark:hover:bg-black dark:hover:bg-opacity-5"
                    [ngClass]="{ unread: !conversation.read }"
                >
                    <!-- Conversation with a link: NotificationNavigationService resolves the target -->
                    @if (conversation.link) {
                        <a
                            class="flex flex-auto cursor-pointer py-5 pl-6"
                            role="link"
                            tabindex="0"
                            (click)="open(conversation)"
                            (keydown.enter)="open(conversation)"
                        >
                            <ng-container
                                *ngTemplateOutlet="conversationContent"
                            ></ng-container>
                        </a>
                    }

                    <!-- Conversation without a link -->
                    @if (!conversation.link) {
                        <div class="flex flex-auto py-5 pl-6">
                            <ng-container
                                *ngTemplateOutlet="conversationContent"
                            ></ng-container>
                        </div>
                    }

                    <!-- Actions -->
                    <div class="relative my-5 ml-2 mr-6 flex flex-col">
                        <!-- Indicator -->
                        <button
                            class="h-6 min-h-6 w-6"
                            mat-icon-button
                            (click)="toggleRead(conversation)"
                            [matTooltip]="
                                conversation.read
                                    ? 'Marcar como no leída'
                                    : 'Marcar como leída'
                            "
                        >
                            <span
                                class="h-2 w-2 rounded-full"
                                [ngClass]="{
                                    'bg-gray-400 dark:bg-gray-500 sm:opacity-0 sm:group-hover:opacity-100':
                                        conversation.read,
                                    'bg-primary': !conversation.read,
                                }"
                            ></span>
                        </button>
                        <!-- Remove -->
                        <button
                            class="h-6 min-h-6 w-6 sm:opacity-0 sm:group-hover:opacity-100"
                            mat-icon-button
                            (click)="delete(conversation)"
                            [matTooltip]="'Eliminar'"
                        >
                            <mat-icon
                                class="icon-size-4"
                                [svgIcon]="'heroicons_solid:x-mark'"
                            ></mat-icon>
                        </button>
                    </div>
                </div>

                <!-- Conversation content template -->
                <ng-template #conversationContent>
                    <!-- Icon -->
                    @if (conversation.icon && !conversation.image) {
                        <div
                            class="mr-4 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-gray-100 dark:bg-gray-700"
                        >
                            <mat-icon
                                class="icon-size-5"
                                [svgIcon]="conversation.icon"
                            >
                            </mat-icon>
                        </div>
                    }
                    <!-- Image -->
                    @if (conversation.image) {
                        <img
                            class="mr-4 h-8 w-8 shrink-0 overflow-hidden rounded-full object-cover object-center"
                            [src]="conversation.image"
                            [alt]="'Conversation image'"
                        />
                    }
                    <!-- Title, description & time -->
                    <div class="flex flex-auto flex-col">
                        <!-- Plain text on purpose: titles/descriptions carry user-typed data -->
                        @if (conversation.title) {
                            <div class="line-clamp-1 font-semibold">
                                {{ conversation.title }}
                            </div>
                        }
                        @if (conversation.description) {
                            <div class="line-clamp-2">
                                {{ conversation.description }}
                            </div>
                        }
                        <div class="text-secondary mt-2 text-sm leading-none">
                            {{ conversation.time | date: 'MMM dd, h:mm a' }}
                        </div>
                    </div>
                </ng-template>
            }

            <!-- No conversations -->
            @if (!conversations || !conversations.length) {
                <div
                    class="flex flex-auto flex-col items-center justify-center px-8 py-12 sm:justify-start"
                >
                    <div
                        class="flex h-14 w-14 flex-0 items-center justify-center rounded-full bg-primary-100 dark:bg-primary-600"
                    >
                        <mat-icon
                            class="text-primary-700 dark:text-primary-50"
                            [svgIcon]="'heroicons_outline:cloud'"
                        ></mat-icon>
                    </div>
                    <div class="mt-5 text-2xl font-semibold tracking-tight">
                        Sin conversaciones
                    </div>
                    <div
                        class="text-secondary mt-1 w-full max-w-60 text-center text-md"
                    >
                        Cuando tengas mensajes nuevos, aparecerán aquí.
                    </div>
                </div>
            }
        </div>
    </div>
</ng-template>
